/**
 * useVehicleOwnership — Histórico de posse e transferência de veículos
 *
 * Expõe:
 *  - `useVehicleOwnershipHistory` → RPC get_vehicle_ownership_history
 *    (lista os períodos de posse: cliente/contato, período, motivo e autor)
 *  - `useTransferVehicle`         → RPC transfer_vehicle_ownership
 *    (transferência atômica de propriedade, com confirmação via p_force)
 *
 * Toda leitura/escrita passa por RPCs SECURITY DEFINER (nunca SELECT direto).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-19 v1.0.0 - Criação inicial
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('VehicleOwnership');

/** Um período de posse do veículo (linha do ledger vehicle_ownerships) */
export interface VehicleOwnershipEntry {
  ownership_id: string;
  customer_id: string | null;
  customer_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  valid_from: string;
  valid_to: string | null;
  transfer_reason: string | null;
  created_by: string | null;
  created_by_name: string | null;
  is_current: boolean;
  total_count: number;
}

/**
 * Histórico de proprietários do veículo (mais recente primeiro).
 * Só consulta quando `enabled` é verdadeiro (aba visível).
 */
export function useVehicleOwnershipHistory(
  vehicleId: string | null,
  enabled = true,
  limit = 50,
  offset = 0,
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['vehicle-ownership-history', tenantId, vehicleId, limit, offset],
    queryFn: async (): Promise<VehicleOwnershipEntry[]> => {
      if (!vehicleId) return [];

      const { data, error } = await supabase.rpc('get_vehicle_ownership_history', {
        p_vehicle_id: vehicleId,
        p_limit: limit,
        p_offset: offset,
        p_tenant_hint: tenantId ?? undefined,
      });

      if (error) {
        logger.error('Falha ao carregar histórico de posse', { error: error.message });
        throw error;
      }

      return (data ?? []) as VehicleOwnershipEntry[];
    },
    enabled: !!vehicleId && !!tenantId && enabled,
    staleTime: 30_000,
  });

  return {
    entries: query.data ?? [],
    total: query.data?.[0]?.total_count ?? 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/** Parâmetros da transferência de propriedade */
export interface TransferVehicleInput {
  vehicleId: string;
  /** Novo cliente (opcional quando há apenas contato) */
  newCustomerId?: string | null;
  /** Novo contato (opcional quando há apenas cliente) */
  newContactId?: string | null;
  /** Motivo livre (máx. 500 caracteres) */
  reason?: string | null;
  /** Data de efeito (ISO). Padrão: agora */
  effectiveAt?: string | null;
  /** Confirmação quando há documentos financeiros após a data de efeito */
  force?: boolean;
}

/**
 * Transferência de propriedade do veículo.
 * A RPC cuida do ledger, do cache em customer_vehicles e da timeline.
 */
export function useTransferVehicle() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TransferVehicleInput) => {
      const { data, error } = await supabase.rpc('transfer_vehicle_ownership', {
        p_vehicle_id: input.vehicleId,
        p_new_customer_id: input.newCustomerId ?? undefined,
        p_new_contact_id: input.newContactId ?? undefined,
        p_reason: input.reason?.trim() ? input.reason.trim().slice(0, 500) : undefined,
        p_effective_at: input.effectiveAt ?? undefined,
        p_force: input.force ?? false,
        p_tenant_hint: currentTenant?.id ?? undefined,
      });

      if (error) {
        logger.warn('Transferência recusada pelo backend', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      // Invalida todas as visões impactadas pela troca de proprietário
      queryClient.invalidateQueries({ queryKey: ['vehicle-ownership-history'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-details'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-activity-log'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-financeiro'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-vehicles-kpis'] });
      logger.info('Veículo transferido', { vehicleId: variables.vehicleId });
    },
  });
}
